/**
 * Briefing (Story 24.10) — `GET ${VITE_HARNESS_BASE_URL}/api/briefing`.
 *
 * One answer to "what is waiting on me" (the viewer's `awaiting-review` rows:
 * an uncapped `total`, an uncapped per-creating-agent `byAgent`, the newest 10
 * `items`) and "what my team finished since yesterday" (the newest 10
 * completions of the last 24h in the plugins the viewer may see). The Tasks
 * badge, the agent tree and Today all read it through `useBriefing()`, the
 * only client, so they never disagree.
 *
 * Story 29.1 adds `unread`: the viewer's conversations with unread agent or
 * platform replies. It is OPTIONAL here so e2e mocks and a harness older than
 * the field (the deploy window) fail soft — `useBriefing` hides the count.
 *
 * Goes through the shared `harnessClient` (the polled read needs the 401 →
 * refresh replay its interceptor owns) with `skipErrorToast`: a failed poll
 * hides the count, it never toasts.
 */

import { harnessClient } from '@/lib/api/client';

/** `name` is `null` for an agent the tenant can no longer see (deleted, archived). */
export interface BriefingAgent {
  id: string;
  name: string | null;
}

export interface BriefingWaitingItem {
  id: string;
  title: string;
  kind: string;
  taskType: string | null;
  pluginId: string;
  /** The agent that CREATED the task; `null` for a human-created one. */
  agent: BriefingAgent | null;
  /** ISO timestamp. */
  createdAt: string;
}

export interface BriefingRecentItem {
  id: string;
  title: string;
  outputSummary: string | null;
  /** ISO timestamp. */
  completedAt: string;
  /** The ASSIGNEE agent; `null` when a person held the task. */
  agent: BriefingAgent | null;
}

export interface Briefing {
  waiting: {
    total: number;
    byAgent: Record<string, number>;
    items: BriefingWaitingItem[];
  };
  recent: {
    items: BriefingRecentItem[];
  };
  /**
   * Story 29.1 — `total` = conversations with ≥1 unread reply; `byAgent` =
   * unread-conversation count per agent id; `byConversation` = unread reply
   * count per advisory id (non-zero entries only). Conversations with an agent
   * the viewer cannot see are never counted. Absent from an older harness.
   */
  unread?: {
    total: number;
    byAgent: Record<string, number>;
    byConversation: Record<string, number>;
  };
}

/** Path on the harness — exposed for unit tests. */
export const BRIEFING_PATH = '/api/briefing';

export async function fetchBriefing(signal?: AbortSignal): Promise<Briefing> {
  const res = await harnessClient.get<Briefing>(BRIEFING_PATH, {
    signal,
    skipErrorToast: true,
  });
  return res.data;
}
